interface RGB {
  r: number;
  g: number;
  b: number;
}

/**
 * Extract dominant colors from an image URL using canvas sampling
 * Returns array of RGB colors for gradient creation
 *
 * Strategy: Use backend image proxy to bypass CORS restrictions.
 * The proxy serves S3 images as same-origin, enabling canvas getImageData().
 */
export const extractDominantColors = (imageUrl: string): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    console.log('🎨 [Color Extraction] Starting extraction for:', imageUrl);

    // Use backend proxy to bypass CORS for S3 images
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:9000';
    const proxyUrl = `${apiUrl}/image-proxy?url=${encodeURIComponent(
      imageUrl,
    )}`;
    console.log('🎨 [Color Extraction] Using proxy URL:', proxyUrl);

    const attemptExtraction = () => {
      const image = new Image();
      // Even though we're using our own proxy, it's on a different port (9000 vs 3000)
      // so we need to set crossOrigin to allow canvas pixel access
      image.crossOrigin = 'anonymous';
      console.log(
        '🎨 [Color Extraction] Loading image through proxy (same-origin)',
      );

      image.onload = () => {
        console.log('🎨 [Color Extraction] ✅ Image loaded successfully');
        try {
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          if (!context) {
            reject(new Error('Canvas context not available'));
            return;
          }

          // Use smaller canvas for performance
          const sampleSize = 100;
          canvas.width = sampleSize;
          canvas.height = sampleSize;

          context.drawImage(image, 0, 0, sampleSize, sampleSize);
          const imageData = context.getImageData(0, 0, sampleSize, sampleSize);
          const pixels = imageData.data;

          const colorCounts: {
            [key: string]: { count: number; saturation: number };
          } = {};

          // Sample every 2nd pixel for better color detection
          for (let i = 0; i < pixels.length; i += 8) {
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            const alpha = pixels[i + 3];

            // Skip transparent pixels
            if (alpha < 125) {
              continue;
            }

            // Calculate brightness and skip very light or very dark pixels
            const brightness = (r + g + b) / 3;
            if (brightness > 235 || brightness < 20) {
              continue;
            }

            // Calculate saturation to prefer vibrant colors
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            const saturation = max === 0 ? 0 : (max - min) / max;

            // Quantize colors with finer precision
            const quantizedR = Math.round(r / 20) * 20;
            const quantizedG = Math.round(g / 20) * 20;
            const quantizedB = Math.round(b / 20) * 20;

            const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
            if (!colorCounts[colorKey]) {
              colorCounts[colorKey] = { count: 0, saturation: 0 };
            }
            colorCounts[colorKey].count += 1;
            colorCounts[colorKey].saturation = Math.max(
              colorCounts[colorKey].saturation,
              saturation,
            );
          }

          // Sort by frequency weighted by saturation to prefer vibrant colors
          const sortedColors = Object.entries(colorCounts)
            .sort(([, dataA], [, dataB]) => {
              const scoreA = dataA.count * (1 + dataA.saturation * 2);
              const scoreB = dataB.count * (1 + dataB.saturation * 2);
              return scoreB - scoreA;
            })
            .slice(0, 8)
            .map(([color]) => {
              const [r, g, b] = color.split(',').map(Number);
              return { r, g, b };
            });

          if (sortedColors.length === 0) {
            console.log(
              '🎨 [Color Extraction] ❌ No colors extracted from image',
            );
            reject(new Error('No colors extracted'));
            return;
          }

          console.log(
            '🎨 [Color Extraction] Extracted color candidates:',
            sortedColors.length,
          );

          // Select 2-3 colors with good contrast for gradient
          const selectedColors: RGB[] = [sortedColors[0]];

          for (
            let i = 1;
            i < sortedColors.length && selectedColors.length < 3;
            i++
          ) {
            const candidate = sortedColors[i];
            const hasGoodContrast = selectedColors.every((existing) => {
              const diff =
                Math.abs(candidate.r - existing.r) +
                Math.abs(candidate.g - existing.g) +
                Math.abs(candidate.b - existing.b);
              return diff > 60;
            });

            if (hasGoodContrast) {
              selectedColors.push(candidate);
            }
          }

          // Darken colors moderately for better text contrast while preserving vibrancy
          const gradientColors = selectedColors.map(({ r, g, b }) => {
            const darkenFactor = 0.8;
            return `rgb(${Math.round(r * darkenFactor)}, ${Math.round(
              g * darkenFactor,
            )}, ${Math.round(b * darkenFactor)})`;
          });

          console.log(
            '🎨 [Color Extraction] ✅ Final gradient colors:',
            gradientColors,
          );

          // Cleanup canvas memory
          canvas.width = 0;
          canvas.height = 0;

          resolve(gradientColors);
        } catch (error) {
          console.error(
            '🎨 [Color Extraction] ❌ Error during color processing:',
            error,
          );
          reject(error);
        }
      };

      image.onerror = (error) => {
        console.error(
          '🎨 [Color Extraction] ❌ Image failed to load through proxy:',
          error,
        );
        console.error('🎨 [Color Extraction] Original URL was:', imageUrl);
        console.error('🎨 [Color Extraction] Proxy URL was:', proxyUrl);
        reject(new Error('Failed to load image through proxy'));
      };

      image.src = proxyUrl;
    };

    // Use proxy for same-origin image loading
    attemptExtraction();
  });
};

/**
 * Create CSS gradient string from color array
 */
export const createGradientString = (colors: string[]): string => {
  if (colors.length === 1) {
    return colors[0];
  }
  return `linear-gradient(135deg, ${colors.join(', ')})`;
};

/**
 * Fallback gradient using warmer neutral colors
 */
export const getFallbackGradient = (): string => {
  return 'linear-gradient(135deg, rgb(100, 60, 60), rgb(140, 80, 80), rgb(160, 100, 100))';
};
